import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import type { Image as MediaImage } from "../../../media/media.interface";
import type { DigitalCardProfileItem } from "../../../digital-cards/digital-cards.interface";
import type {
  PrintCardElement,
  PrintCardFontPreset,
  PrintCardTextElement,
  PrintCardTextSquashPart,
  PrintCardTextSquashStyle,
} from "../../print-cards.interface";
import {
  PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT,
  PRINT_CARD_DEFAULT_MIN_FONT_SIZE_PT,
  PRINT_CARD_DEFAULT_SQUASH_SEPARATOR,
  PRINT_CARD_FONT_WEIGHT_OPTIONS,
  type PrintCardFontWeight,
  type PrintCardTextAlign,
  type PrintCardTextFit,
  type PrintCardVerticalAlign,
} from "../../print-cards.interface";
import {
  PRINT_CARD_PALETTE_BUILTIN_TEXT_VARIABLES,
  PRINT_CARD_VARIABLE_PLACEHOLDER_KEY,
  isPrintCardPaletteBuiltinTextVariable,
  printCardCssPositionFromVisual,
  printCardElementDefaultName,
  printCardTextElementSquashParts,
  printCardVisualPosition,
} from "../../print-card.util";
import { DomainVariablesDialogComponent } from "../../../shared/domain-variables-dialog/domain-variables-dialog.component";
import type { DomainVariablesDialogData } from "../../../shared/domain-variables-dialog/domain-variables-dialog.interface";
import { mediaFilePreview } from "../../../shared/utils/media-file-preview.util";

const INPUT_DEBOUNCE_MS = 280;

@Component({
  selector: "app-print-card-properties-panel",
  templateUrl: "./print-card-properties-panel.component.html",
  styleUrls: ["./print-card-properties-panel.component.scss"],
  standalone: false,
})
export class PrintCardPropertiesPanelComponent implements OnChanges, OnDestroy {
  @Input() element: PrintCardElement | null = null;
  @Input() activeSide: "front" | "back" = "front";
  @Input() backgroundColor = "#FFFFFF";
  @Input() backgroundImageUrl: string | null = null;
  @Input() mediaImages: MediaImage[] = [];
  @Input() mediaImagesLoading = false;
  @Input() fontPresets: PrintCardFontPreset[] = [];
  @Input() fontPresetId = "roboto";
  @Input() companyBrandFontReady = false;
  @Input() digitalCardTemplates: DigitalCardProfileItem[] = [];
  @Input() digitalCardProfileId: string | null = null;
  readonly digitalCardTemplateLabel = (template: DigitalCardProfileItem) =>
    template.displayName || template.profileId || "";
  readonly digitalCardTemplateValue = (template: DigitalCardProfileItem) =>
    template.profileId;
  @Input() squashTargets: Array<{ id: string; label: string }> = [];
  @Input() domainVariables: string[] = [];
  @Output() patchElement = new EventEmitter<Partial<PrintCardElement>>();
  @Output() backgroundColorChange = new EventEmitter<string>();
  @Output() backgroundImageUrlChange = new EventEmitter<string>();
  @Output() fontPresetChange = new EventEmitter<string>();
  @Output() digitalCardProfileIdChange = new EventEmitter<string | null>();
  @Output() squashWithElement = new EventEmitter<string>();
  @Output() unsquashElement = new EventEmitter<void>();

  squashPartnerId = "";
  readonly propsPanelOpen: Record<
    "font" | "digitalCard" | "sideBackground" | "properties" | "squash",
    boolean
  > = {
    font: false,
    digitalCard: false,
    sideBackground: true,
    properties: true,
    squash: true,
  };

  togglePropsPanel(
    key: "font" | "digitalCard" | "sideBackground" | "properties" | "squash",
  ): void {
    this.propsPanelOpen[key] = !this.propsPanelOpen[key];
  }

  constructor(private readonly dialog: MatDialog) {}

  readonly fontWeights = PRINT_CARD_FONT_WEIGHT_OPTIONS;
  readonly aligns: PrintCardTextAlign[] = ["left", "center", "right"];
  readonly valigns: PrintCardVerticalAlign[] = ["top", "middle", "bottom"];
  readonly textFits: PrintCardTextFit[] = ["none", "shrink"];
  readonly mediaFilePreview = mediaFilePreview;
  readonly defaultFontSizePt = PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT;

  private readonly drafts: Record<string, string> = {};
  private readonly fieldTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes["element"] || changes["element"].firstChange) {
      return;
    }
    const prev = changes["element"].previousValue as PrintCardElement | null;
    const next = changes["element"].currentValue as PrintCardElement | null;
    if (prev?.id === next?.id) {
      return;
    }
    for (const timer of this.fieldTimers.values()) {
      clearTimeout(timer);
    }
    this.fieldTimers.clear();
    for (const key of Object.keys(this.drafts)) {
      delete this.drafts[key];
    }
  }

  ngOnDestroy(): void {
    for (const timer of this.fieldTimers.values()) {
      clearTimeout(timer);
    }
    this.fieldTimers.clear();
  }

  fieldValue(key: string, current: string | number | null | undefined): string {
    return key in this.drafts ? this.drafts[key] : String(current ?? "");
  }

  onFieldInput(key: string, value: string): void {
    this.drafts[key] = value;
    const previous = this.fieldTimers.get(key);
    if (previous != null) {
      clearTimeout(previous);
    }
    this.fieldTimers.set(
      key,
      setTimeout(() => {
        this.fieldTimers.delete(key);
        const latest = this.drafts[key] ?? value;
        delete this.drafts[key];
        this.commitField(key, latest);
      }, INPUT_DEBOUNCE_MS),
    );
  }

  onFieldBlur(key: string, value: string): void {
    const previous = this.fieldTimers.get(key);
    if (previous != null) {
      clearTimeout(previous);
      this.fieldTimers.delete(key);
    }
    delete this.drafts[key];
    this.commitField(key, value);
  }

  private commitField(key: string, value: string): void {
    if (key === "name") {
      if (!this.element) {
        return;
      }
      const trimmed = value.trim();
      this.patchElement.emit({
        name: trimmed || printCardElementDefaultName(this.element),
      });
      return;
    }

    if (key === "xCm" || key === "yCm") {
      if (!this.element) {
        return;
      }
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n)) {
        return;
      }
      const visual = printCardVisualPosition(this.element);
      const css = printCardCssPositionFromVisual({
        visualXCm: key === "xCm" ? n : visual.xCm,
        visualYCm: key === "yCm" ? n : visual.yCm,
        widthCm: this.element.widthCm,
        heightCm: this.element.heightCm,
        rotationDeg: this.element.rotationDeg,
      });
      this.patchElement.emit({ xCm: css.xCm, yCm: css.yCm });
      return;
    }

    if (key === "widthCm" || key === "heightCm" || key === "fontSizePt") {
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n < 0 || !this.element) {
        return;
      }
      const rounded = Math.round(n * 100) / 100;
      if (key === "fontSizePt" && this.element.type === "text") {
        const patch: Partial<PrintCardElement> = { fontSizePt: rounded };
        if (
          this.element.textFit === "shrink" &&
          typeof this.element.minFontSizePt === "number" &&
          this.element.minFontSizePt > rounded
        ) {
          patch.minFontSizePt = rounded;
        }
        this.patchElement.emit(patch);
        return;
      }
      if (
        (this.element.type === "qr" || this.element.type === "nfc") &&
        (key === "widthCm" || key === "heightCm")
      ) {
        this.patchElement.emit({ widthCm: rounded, heightCm: rounded });
        return;
      }
      if (key === "widthCm" || key === "heightCm") {
        const visual = printCardVisualPosition(this.element);
        const widthCm = key === "widthCm" ? rounded : this.element.widthCm;
        const heightCm = key === "heightCm" ? rounded : this.element.heightCm;
        const css = printCardCssPositionFromVisual({
          visualXCm: visual.xCm,
          visualYCm: visual.yCm,
          widthCm,
          heightCm,
          rotationDeg: this.element.rotationDeg,
        });
        this.patchElement.emit({
          widthCm,
          heightCm,
          xCm: css.xCm,
          yCm: css.yCm,
        });
      }
      return;
    }

    if (key === "rotationDeg") {
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n)) {
        return;
      }
      let rotationDeg = n % 360;
      if (rotationDeg > 180) {
        rotationDeg -= 360;
      } else if (rotationDeg <= -180) {
        rotationDeg += 360;
      }
      rotationDeg = Math.round(rotationDeg * 10) / 10;
      this.patchElement.emit({
        rotationDeg: rotationDeg === 0 ? undefined : rotationDeg,
      });
      return;
    }

    if (key === "text") {
      this.patchElement.emit({ text: value });
      return;
    }

    if (key === "maxLines") {
      if (this.element?.type !== "text") {
        return;
      }
      const n = Number.parseInt(value.trim(), 10);
      this.patchElement.emit({
        maxLines:
          Number.isFinite(n) && n >= 1
            ? Math.min(20, Math.floor(n))
            : undefined,
      });
      return;
    }

    if (key === "lineHeight") {
      if (this.element?.type !== "text") {
        return;
      }
      const trimmed = value.trim();
      if (!trimmed) {
        this.patchElement.emit({ lineHeight: undefined });
        return;
      }
      const n = Number.parseFloat(trimmed);
      if (!Number.isFinite(n) || n < 0) {
        return;
      }
      const lineHeight = Math.round(n * 100) / 100;
      this.patchElement.emit({
        lineHeight: lineHeight === 1 ? undefined : lineHeight,
      });
      return;
    }

    if (key === "minFontSizePt") {
      if (this.element?.type !== "text") {
        return;
      }
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n < 0) {
        return;
      }
      const fontSizePt =
        typeof this.element.fontSizePt === "number" &&
        this.element.fontSizePt >= 0
          ? this.element.fontSizePt
          : n;
      this.patchElement.emit({
        minFontSizePt: Math.min(fontSizePt, Math.round(n * 10) / 10),
      });
      return;
    }

    if (key === "borderRadiusMm") {
      if (this.element?.type !== "shape") {
        return;
      }
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n < 0) {
        return;
      }
      const borderRadiusMm = Math.min(50, Math.round(n * 10) / 10);
      this.patchElement.emit({
        borderRadiusMm: borderRadiusMm > 0 ? borderRadiusMm : undefined,
      });
      return;
    }

    if (key === "squashSeparator") {
      if (!this.textElement || !this.isSquashElement) {
        return;
      }
      this.patchElement.emit({
        variable: "squash",
        squashParts: this.squashParts,
        squashSeparator: value,
        squashSeparatorStyle: this.squashSeparatorStyle,
        text: undefined,
        lineHeight: undefined,
      });
      return;
    }

    if (key === "squashSeparatorFontSizePt") {
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n < 0) {
        return;
      }
      this.patchSquashSeparatorStyle({
        fontSizePt: Math.round(n * 10) / 10,
      });
      return;
    }

    if (key === "squashSeparatorLineHeight") {
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n < 0) {
        return;
      }
      const lineHeight = Math.round(n * 100) / 100;
      this.patchSquashSeparatorStyle({
        lineHeight: lineHeight === 1 ? undefined : lineHeight,
      });
      return;
    }

    if (key.startsWith("squashFontSizePt-")) {
      const index = Number.parseInt(key.slice("squashFontSizePt-".length), 10);
      if (!Number.isFinite(index)) {
        return;
      }
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n < 0) {
        return;
      }
      this.patchSquashPart(index, { fontSizePt: Math.round(n * 10) / 10 });
      return;
    }

    if (key.startsWith("squashLineHeight-")) {
      const index = Number.parseInt(key.slice("squashLineHeight-".length), 10);
      if (!Number.isFinite(index)) {
        return;
      }
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n < 0) {
        return;
      }
      const lineHeight = Math.round(n * 100) / 100;
      this.patchSquashPart(index, {
        lineHeight: lineHeight === 1 ? undefined : lineHeight,
      });
    }
  }

  get trimmedBackgroundImageUrl(): string {
    return this.backgroundImageUrl?.trim() || "";
  }

  get orphanBackgroundImageUrl(): string | null {
    return this.orphanMediaUrl(this.trimmedBackgroundImageUrl);
  }

  get mediaSourceValue(): string {
    if (this.element?.type === "logo" || this.element?.type === "image") {
      return this.element.srcUrl?.trim() || "";
    }
    return "";
  }

  get orphanMediaSourceUrl(): string | null {
    return this.orphanMediaUrl(this.mediaSourceValue);
  }

  get showsTextContent(): boolean {
    return (
      this.element?.type === "text" &&
      (this.element.variable === "custom" ||
        this.element.variable === "tagline")
    );
  }

  get textElement(): PrintCardTextElement | null {
    return this.element?.type === "text" ? this.element : null;
  }

  get isSquashElement(): boolean {
    return (
      this.textElement != null &&
      printCardTextElementSquashParts(this.textElement) != null
    );
  }

  get showsVariableSelect(): boolean {
    return (
      this.textElement != null &&
      !this.isSquashElement &&
      !this.showsTextContent &&
      !isPrintCardPaletteBuiltinTextVariable(this.textElement.variable)
    );
  }

  get variableOptions(): string[] {
    const current = this.textElement?.variable?.trim() || "";
    const options = new Set(
      this.domainVariables.filter(
        (name) => !isPrintCardPaletteBuiltinTextVariable(name),
      ),
    );
    if (
      current &&
      current !== "custom" &&
      current !== "tagline" &&
      current !== "squash" &&
      !isPrintCardPaletteBuiltinTextVariable(current)
    ) {
      options.add(current);
    }
    return [...options].sort((a, b) => a.localeCompare(b));
  }

  get squashParts(): PrintCardTextSquashPart[] {
    return this.textElement
      ? (printCardTextElementSquashParts(this.textElement) ?? [])
      : [];
  }

  get squashSeparatorValue(): string {
    const el = this.textElement;
    return typeof el?.squashSeparator === "string"
      ? el.squashSeparator
      : PRINT_CARD_DEFAULT_SQUASH_SEPARATOR;
  }

  get squashSeparatorStyle(): PrintCardTextSquashStyle {
    return this.textElement?.squashSeparatorStyle ?? {};
  }

  get canSquashWithOther(): boolean {
    return this.textElement != null && this.squashTargets.length > 0;
  }

  get elementName(): string {
    if (!this.element) {
      return "";
    }
    return (
      this.element.name?.trim() || printCardElementDefaultName(this.element)
    );
  }

  get visualXCm(): number {
    return this.element ? printCardVisualPosition(this.element).xCm : 0;
  }

  get visualYCm(): number {
    return this.element ? printCardVisualPosition(this.element).yCm : 0;
  }

  get textFitValue(): PrintCardTextFit {
    return this.element?.type === "text" && this.element.textFit === "shrink"
      ? "shrink"
      : "none";
  }

  get elementAlignValue(): PrintCardTextAlign {
    const el = this.element;
    if (!el) {
      return "left";
    }
    if (el.type === "text" || el.type === "logo" || el.type === "image") {
      if (
        el.align === "left" ||
        el.align === "center" ||
        el.align === "right"
      ) {
        return el.align;
      }
      return el.type === "logo" && this.activeSide === "back"
        ? "center"
        : "left";
    }
    return "left";
  }

  get elementValignValue(): PrintCardVerticalAlign {
    const el = this.element;
    if (!el) {
      return "top";
    }
    if (el.type === "text") {
      return el.valign === "middle" || el.valign === "bottom"
        ? el.valign
        : "top";
    }
    if (el.type === "logo" || el.type === "image") {
      return el.valign === "top" || el.valign === "bottom"
        ? el.valign
        : "middle";
    }
    return "top";
  }

  get maxLinesValue(): number | "" {
    return this.element?.type === "text" ? (this.element.maxLines ?? "") : "";
  }

  get lineHeightValue(): number {
    const el = this.element;
    if (el?.type !== "text") {
      return 1;
    }
    return typeof el.lineHeight === "number" ? el.lineHeight : 1;
  }

  get minFontSizeValue(): number | "" {
    return this.element?.type === "text"
      ? (this.element.minFontSizePt ?? "")
      : "";
  }

  get minFontSizePtCeiling(): number {
    if (this.element?.type !== "text") {
      return 0;
    }
    if (this.isSquashElement) {
      const sizes = [
        ...this.squashParts.map((part) => part.fontSizePt),
        this.squashSeparatorStyle.fontSizePt,
      ].filter((size): size is number => typeof size === "number" && size > 0);
      if (sizes.length > 0) {
        return Math.max(...sizes);
      }
    }
    return typeof this.element.fontSizePt === "number" &&
      this.element.fontSizePt > 0
      ? this.element.fontSizePt
      : 0;
  }

  mediaLabel(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) {
      return "";
    }
    return (
      this.mediaImages
        .find((image) => image.imageUrl === trimmed)
        ?.name?.trim() || trimmed
    );
  }

  onReverse(checked: boolean): void {
    this.patchElement.emit({ reversed: checked ? true : undefined });
  }

  onColor(value: string): void {
    if (this.element?.type === "text" || this.element?.type === "nfc") {
      this.patchElement.emit({ color: value });
    }
  }

  onFill(value: string): void {
    if (this.element?.type === "shape") {
      this.patchElement.emit({ fill: value });
    }
  }

  onMediaSource(value: string): void {
    if (this.element?.type === "logo") {
      const trimmed = value.trim();
      this.patchElement.emit(
        trimmed
          ? { srcUrl: trimmed, srcVariable: undefined }
          : { srcUrl: undefined, srcVariable: "companyImageUrl" },
      );
      return;
    }
    if (this.element?.type === "image") {
      this.patchElement.emit({ srcUrl: value.trim() || undefined });
    }
  }

  onWeight(value: string): void {
    const weight = Number.parseInt(value, 10) as PrintCardFontWeight;
    if (!this.fontWeights.includes(weight)) {
      return;
    }
    this.patchElement.emit({ fontWeight: weight });
  }

  onTextFit(value: string): void {
    if (this.element?.type !== "text") {
      return;
    }
    if (value !== "shrink") {
      this.patchElement.emit({ textFit: "none", minFontSizePt: undefined });
      return;
    }
    const fontSizePt =
      typeof this.element.fontSizePt === "number" &&
      this.element.fontSizePt >= 0
        ? this.element.fontSizePt
        : this.defaultFontSizePt;
    const minFontSizePt =
      typeof this.element.minFontSizePt === "number" &&
      this.element.minFontSizePt >= 0
        ? this.element.minFontSizePt
        : PRINT_CARD_DEFAULT_MIN_FONT_SIZE_PT;
    this.patchElement.emit({
      textFit: "shrink",
      minFontSizePt: Math.min(fontSizePt, minFontSizePt),
    });
  }

  onAlign(value: string): void {
    if (value === "left" || value === "center" || value === "right") {
      this.patchElement.emit({ align: value });
    }
  }

  onValign(value: string): void {
    if (value === "top" || value === "middle" || value === "bottom") {
      this.patchElement.emit({ valign: value });
    }
  }

  openDomainVariablesDialog(): void {
    const variables = [
      ...new Set([
        ...PRINT_CARD_PALETTE_BUILTIN_TEXT_VARIABLES,
        ...this.domainVariables,
      ]),
    ].sort((a, b) => a.localeCompare(b));
    this.dialog.open<DomainVariablesDialogComponent, DomainVariablesDialogData>(
      DomainVariablesDialogComponent,
      {
        width: "480px",
        panelClass: "app-dialog-panel",
        data: {
          variables,
          placeholderKeys: PRINT_CARD_VARIABLE_PLACEHOLDER_KEY,
        },
      },
    );
  }

  onSquashPartWeight(index: number, value: string): void {
    const weight = Number.parseInt(value, 10) as PrintCardFontWeight;
    if (!this.fontWeights.includes(weight)) {
      return;
    }
    this.patchSquashPart(index, { fontWeight: weight });
  }

  onSquashPartColor(index: number, value: string): void {
    this.patchSquashPart(index, { color: value });
  }

  onSquashPartFlag(
    index: number,
    key: "italic" | "uppercase",
    checked: boolean,
  ): void {
    this.patchSquashPart(index, { [key]: checked || undefined });
  }

  onSquashSeparatorWeight(value: string): void {
    const weight = Number.parseInt(value, 10) as PrintCardFontWeight;
    if (!this.fontWeights.includes(weight)) {
      return;
    }
    this.patchSquashSeparatorStyle({ fontWeight: weight });
  }

  onSquashSeparatorColor(value: string): void {
    this.patchSquashSeparatorStyle({ color: value });
  }

  onSquashSeparatorFlag(key: "italic" | "uppercase", checked: boolean): void {
    this.patchSquashSeparatorStyle({ [key]: checked || undefined });
  }

  onApplySquash(): void {
    if (!this.squashPartnerId.trim()) {
      return;
    }
    this.squashWithElement.emit(this.squashPartnerId);
    this.squashPartnerId = "";
  }

  onUnsquash(): void {
    this.unsquashElement.emit();
  }

  private orphanMediaUrl(url: string): string | null {
    const trimmed = url.trim();
    if (!trimmed) {
      return null;
    }
    return this.mediaImages.some((image) => image.imageUrl === trimmed)
      ? null
      : trimmed;
  }

  private patchSquashPart(
    index: number,
    patch: Partial<PrintCardTextSquashPart>,
  ): void {
    if (!this.textElement || !this.isSquashElement) {
      return;
    }
    const inherited =
      typeof this.textElement.lineHeight === "number" &&
      this.textElement.lineHeight >= 0 &&
      this.textElement.lineHeight !== 1
        ? this.textElement.lineHeight
        : undefined;
    const squashParts = this.squashParts.map((part, partIndex) => {
      const withInherited =
        inherited != null && part.lineHeight == null
          ? { ...part, lineHeight: inherited }
          : { ...part };
      const next =
        partIndex === index ? { ...withInherited, ...patch } : withInherited;
      if (
        partIndex === index &&
        "lineHeight" in patch &&
        patch.lineHeight === undefined
      ) {
        delete next.lineHeight;
      }
      return next;
    });
    const squashSeparatorStyle = { ...this.squashSeparatorStyle };
    if (inherited != null && squashSeparatorStyle.lineHeight == null) {
      squashSeparatorStyle.lineHeight = inherited;
    }
    this.patchElement.emit({
      variable: "squash",
      squashParts,
      squashSeparator: this.squashSeparatorValue,
      squashSeparatorStyle,
      text: undefined,
      fontSizePt: undefined,
      fontWeight: undefined,
      italic: undefined,
      uppercase: undefined,
      color: undefined,
      lineHeight: undefined,
    });
  }

  private patchSquashSeparatorStyle(
    patch: Partial<PrintCardTextSquashStyle>,
  ): void {
    if (!this.textElement || !this.isSquashElement) {
      return;
    }
    const inherited =
      typeof this.textElement.lineHeight === "number" &&
      this.textElement.lineHeight >= 0 &&
      this.textElement.lineHeight !== 1
        ? this.textElement.lineHeight
        : undefined;
    const squashParts = this.squashParts.map((part) =>
      inherited != null && part.lineHeight == null
        ? { ...part, lineHeight: inherited }
        : part,
    );
    const squashSeparatorStyle = {
      ...this.squashSeparatorStyle,
      ...(inherited != null && this.squashSeparatorStyle.lineHeight == null
        ? { lineHeight: inherited }
        : {}),
      ...patch,
    };
    if ("lineHeight" in patch && patch.lineHeight === undefined) {
      delete squashSeparatorStyle.lineHeight;
    }
    this.patchElement.emit({
      variable: "squash",
      squashParts,
      squashSeparator: this.squashSeparatorValue,
      squashSeparatorStyle,
      text: undefined,
      fontSizePt: undefined,
      fontWeight: undefined,
      italic: undefined,
      uppercase: undefined,
      color: undefined,
      lineHeight: undefined,
    });
  }
}
